import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import FormModal from '../../core/containers/FormModal';
import { QuickSearch } from '../../core/components/search';
import { SimpleSelectInput } from 'modules/core/components/forms';
import { SpinnerOverlay } from 'modules/core/components/spinner';
import { SERVICE_68_TIRE_FITTING_STATIONARY, SERVICE_69_TIRE_STORAGE_STATIONARY } from 'modules/core/constants/services';
import { priceListActions } from '../store/priceListStore';
import { priceListItemsActions, PriceListItem } from '../store/priceListItemsStore';
import {
  PriceList, PriceListItemCalculateComplexTableInterface,
} from '../api/priceListApi';
import PriceListViewComplexTable from './PriceListViewComplexTable';
import PriceListViewStorageTable from './PriceListViewStorageTable';
import PriceListViewTable from './PriceListViewTable';

interface PriceListOfficeViewModalProps {
  show: boolean;
  onClose: () => void;
  officeId: string;
  officeName?: string;
  /** Задел под сервис 69 (хранение шин): комплексы показываем только у шиномонтажа */
  serviceId?: number;
}

// Планшет (576–991px): модалка прайса во всю ширину экрана с полями 0.5rem —
// стандартные поля .modal-xl (1.75rem) и max-width оставляют матрице цен мало места.
// Класс навешивается через dialogClassName FormModal; правило идёт после CSS Bootstrap
// и перебивает .modal-xl без !important. Телефон (поля уже 0.5rem) и десктоп не трогаем.
const DIALOG_STYLES = `
  @media (min-width: 576px) and (max-width: 991px) {
    .price-list-office-view-dialog { max-width: calc(100% - 1rem); margin: 0.5rem auto; }
  }
`;

// Прайс хранения (69) того же офиса для блока «Цены хранения». Отсутствие прайса
// хранения — не ошибка (офис может не оказывать хранение): блок просто не показываем
const loadStoragePriceItems = async (partnerOfficeId: string): Promise<PriceListItem[]> => {
  try {
    const storageList = await priceListActions.getPriceListsStationaryForInvoices({
      partnerOfficeId,
      serviceId: SERVICE_69_TIRE_STORAGE_STATIONARY,
    });
    if (!storageList || 'errorCode' in storageList || !storageList.id) return [];
    const itemsRes = await priceListItemsActions.getPriceListItemsData({ priceListId: storageList.id });
    return itemsRes?.errorData ? [] : (itemsRes?.data ?? []);
  } catch {
    return [];
  }
};

// Просмотр прайс-листа офиса (кнопка «Посмотреть прайс» в шапке): комплексы +
// розничные цены + дополнительные позиции, всё только чтение. Данные — в локальном
// state (экшены-возвраты priceListItemsStore), общий стор не затирается
const PriceListOfficeViewModal = (props: PriceListOfficeViewModalProps) => {
  const {
    show, onClose, officeId, officeName, serviceId = SERVICE_68_TIRE_FITTING_STATIONARY,
  } = props;

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string>(null);
  const [priceList, setPriceList] = useState<PriceList>(null);
  const [items, setItems] = useState<PriceListItem[]>([]);
  const [complexItems, setComplexItems] = useState<PriceListItemCalculateComplexTableInterface[]>([]);
  const [storageItems, setStorageItems] = useState<PriceListItem[]>([]);
  const [classifier2Filter, setClassifier2Filter] = useState('Легковой');
  const [searchValue, setSearchValue] = useState('');

  const isTireFitting = serviceId === SERVICE_68_TIRE_FITTING_STATIONARY;

  const loadData = useCallback(async () => {
    if (!officeId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const list = await priceListActions.getPriceListsStationaryForInvoices({
        partnerOfficeId: officeId,
        serviceId,
      });
      if (!list || 'errorCode' in list || !list.id) {
        setLoadError('Для офиса не найден прайс-лист (проверьте привязку услуги к офису)');
        setPriceList(null);
        return;
      }
      setPriceList(list);

      const [itemsRes, complexesRes, storageItemsRes] = await Promise.all([
        priceListItemsActions.getPriceListItemsData({ priceListId: list.id }),
        isTireFitting
          ? priceListItemsActions.getCalculateComplexTableListData(list.id)
          : Promise.resolve(null),
        // «Цены хранения» — вместе с прайсом шиномонтажа; при открытии модалки
        // по самому хранению (69 с планировщика) не грузим: там розничные цены
        // уже и есть цены хранения
        isTireFitting
          ? loadStoragePriceItems(officeId)
          : Promise.resolve([]),
      ]);

      if (itemsRes?.errorData) {
        setLoadError('Не удалось загрузить позиции прайс-листа');
        return;
      }
      setItems(itemsRes?.data ?? []);
      setComplexItems(complexesRes?.errorData ? [] : (complexesRes?.data?.items ?? []));
      setStorageItems(storageItemsRes ?? []);
    } catch (e) {
      setLoadError(e?.message || 'Не удалось загрузить данные прайса офиса');
    } finally {
      setLoading(false);
    }
  }, [officeId, serviceId, isTireFitting]);

  useEffect(() => {
    if (show) {
      setClassifier2Filter('Легковой');
      setSearchValue('');
      setLoadError(null);
      setStorageItems([]);
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, loadData]);

  const classifier2SelectData = useMemo(() => {
    const set = new Set<string>();
    items
      .filter(i => i.classifier1RefValueName)
      .forEach(i => { if (i.classifier2RefValueName) set.add(i.classifier2RefValueName); });
    return Array.from(set).sort()
      .map(v => ({ id: v, name: v }));
  }, [items]);

  const handleChangeQuickSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target || {};
    setSearchValue((value?.length > 0) ? value.toUpperCase() : '');
  };

  const handleSubmitQuickSearch = (value: string) => {
    handleChangeQuickSearch({ target: { value } } as any);
  };

  const header = (
    <>
      { `Прайс-лист${ priceList?.name ? `: ${ priceList.name }` : '' }` }
      { officeName && <span className='text-muted small ml-2'>{ officeName }</span> }
    </>
  );

  return (
    <FormModal
      show={ show }
      size='xl'
      dialogClassName='price-list-office-view-dialog'
      header={ header }
      initialValues={ {} }
      showSubmitButton={ false }
      cancelButtonTitle='Закрыть'
      showRequiredFieldsMessage={ false }
      scrollable
      onClose={ onClose }
      onCancel={ onClose }
    >
      <style>{ DIALOG_STYLES }</style>

      <SpinnerOverlay loading={ loading } outerHeight='150px' marginBottom='0' />

      { !loading && loadError && (
        <div className='text-danger p-3'>{ loadError }</div>
      ) }

      { !loading && !loadError && priceList && (
        <>
          <Row className='mb-3'>
            <Col md={ 4 }>
              <SimpleSelectInput
                label='Тип автомобиля'
                value={ classifier2Filter }
                name='classifier2'
                data={ classifier2SelectData }
                dataTitle='name'
                dataValue='id'
                placeholder='Выберите тип'
                onChange={ (event: React.ChangeEvent<HTMLSelectElement>) => {
                  setClassifier2Filter(event.target.value);
                } }
              />
            </Col>
            <Col md={ 4 } className='d-flex align-items-end'>
              <QuickSearch
                name='searchText'
                value={ searchValue }
                placeholder='Быстрый поиск'
                disabled={ loading }
                onClear={ handleSubmitQuickSearch }
                onChange={ handleChangeQuickSearch }
                onSubmit={ handleSubmitQuickSearch }
              />
            </Col>
          </Row>

          { isTireFitting && (
            <PriceListViewComplexTable
              priceListId={ priceList.id }
              items={ complexItems }
              vehicleTypeRefValueName={ classifier2Filter }
            />
          ) }

          { /* Хранение (69) того же офиса: перед «Розничными ценами»; пусто, если офис
              не оказывает хранение или у прайса хранения нет позиций */ }
          <PriceListViewStorageTable items={ storageItems } />

          <PriceListViewTable
            items={ items }
            classifier2Filter={ classifier2Filter }
            searchValue={ searchValue }
            composition={ priceList.composition }
          />
        </>
      ) }
    </FormModal>
  );
};

export default PriceListOfficeViewModal;
